import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Stack,
  Container,
  Box,
  Hidden,
  Grid,
  Divider,
  TextField,
  Snackbar,
  MenuItem,
  Menu,
  createTheme, 
  ThemeProvider,
  CssBaseline, 
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import InstagramIcon from '@mui/icons-material/Instagram';
import MenuIcon from '@mui/icons-material/Menu';
import { useTheme, alpha } from '@mui/material/styles'; 
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import { collection, onSnapshot } from "firebase/firestore";
import emailjs from 'emailjs-com';
import { motion } from 'framer-motion';
import Typed from 'typed.js'; 

import manUsingLaptop from "./assets/man_using_laptop.jpg";
import profile from "./assets/profile.jpeg";
import db from "./firebase";


const collectionRef = collection(db, "projects");

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#EF6D58', 
      light: '#FF8A65',
      dark: '#D84315',
    },
    secondary: {
      main: '#00BFFF', 
      light: '#65DFFF',
      dark: '#008FC7',
    },
    background: {
      default: '#1A202C', 
      paper: '#2D3748', 
    },
    text: {
      primary: '#E2E8F0',
      secondary: '#A0AEC0', 
      disabled: '#718096',
    },
    divider: alpha('#E2E8F0', 0.12), 
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
    h1: {
      fontWeight: 900,
    },
    h2: {
      fontWeight: 800,
    },
    h4: {
      fontWeight: 700,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            color: '#EF6D58',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: 'rgba(255,255,255,0.05)', 
            '& fieldset': {
              borderColor: alpha('#E2E8F0', 0.3), 
            },
            '&:hover fieldset': {
              borderColor: alpha('#E2E8F0', 0.6), 
            },
            '&.Mui-focused fieldset': {
              borderColor: '#EF6D58', 
              borderWidth: '2px', 
            },
          },
          '& .MuiInputLabel-root': {
            color: '#A0AEC0',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#EF6D58', 
          },
        },
      },
    },
  },
});

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const Span = ({ title }) => {
  const theme = useTheme(); 
  return <span style={{ color: theme.palette.primary.main }}> {title} </span>;
};

const Social = ({ isWhite = false }) => {
  const theme = useTheme();
  const iconColor = isWhite ? "white" : theme.palette.text.primary; 

  return (
    <Stack direction="row" spacing={3}>
      <motion.div whileHover={{ scale: 1.3, rotate: 5 }} whileTap={{ scale: 0.9 }}>
        <IconButton
          href="https://www.linkedin.com/in/nijat-zeynalli-9295b2289/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="LinkedIn"
        >
          <LinkedInIcon
            sx={{ color: iconColor }}
            fontSize="large"
          />
        </IconButton>
      </motion.div>
      <motion.div whileHover={{ scale: 1.3, rotate: -5 }} whileTap={{ scale: 0.9 }}>
        <IconButton
          href="https://github.com/zeynalnicat"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="GitHub"
        >
          <GitHubIcon
            sx={{ color: iconColor }}
            fontSize="large"
          />
        </IconButton>
      </motion.div>
      <motion.div whileHover={{ scale: 1.3, rotate: 5 }} whileTap={{ scale: 0.9 }}>
        <IconButton
          href="https://www.instagram.com/affuross/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Instagram"
        >
          <InstagramIcon
            sx={{ color: iconColor }}
            fontSize="large"
          />
        </IconButton>
      </motion.div>
    </Stack>
  );
};

// Header component
function Header() {
  const theme = useTheme();
  const typedElement = useRef(null);

  useEffect(() => {
    const typed = new Typed(typedElement.current, {
      strings: ['Mobile Development', 'Frontend Development', 'Android Development','Data Science','Machine Learning'],
      typeSpeed: 50,
      backSpeed: 30,
      loop: true,
      showCursor: true,
      cursorChar: '|',
    });

    return () => {
      typed.destroy();
    };
  }, []);

  const headerVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  const textVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, delay: 0.3, ease: "easeOut" } },
  };

  const imageVariants = {
    hidden: { opacity: 0, x: 50, scale: 0.8 },
    visible: { opacity: 1, x: 0, scale: 1, transition: { duration: 0.8, delay: 0.5, ease: "easeOut" } },
  };

  // For smooth scrolling
  const scrollToSection = (id) => {
    document.getElementById(id).scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Stack
      height="100vh"
      sx={{
        backgroundColor: theme.palette.background.default,
        backgroundImage: `radial-gradient(at 50% 0%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
        backgroundRepeat: 'no-repeat',
      }}
    >
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar sx={{ justifyContent: 'space-between', paddingTop: 2 }}>
          <motion.div initial="hidden" animate="visible" variants={headerVariants}>
            <Typography
              fontSize={{ sm: 25, xs: 20 }}
              fontWeight={900}
              color={theme.palette.text.primary}
            >
              <a style={{ color: theme.palette.text.primary, textDecoration: 'none' }} href="/">
                About <Span title="Me" />
              </a>
            </Typography>
          </motion.div>
          <Hidden smDown>
            <Stack
              direction="row"
              spacing={3}
              sx={{
                "& >a": {
                  textDecoration: "none",
                  color: theme.palette.text.primary,
                  fontSize: "16px",
                  position: 'relative',
                  paddingBottom: '5px',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    width: '0',
                    height: '2px',
                    bottom: '0',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: theme.palette.primary.main,
                    transition: 'width 0.3s ease-in-out',
                  },
                  '&:hover::after': {
                    width: '100%',
                  },
                },
              }}
            >
              <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}>About</a>
              <a href="#projects" onClick={(e) => { e.preventDefault(); scrollToSection('projects'); }}>Projects</a>
              <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>Contact me</a>
              <a href="https://drive.google.com/file/d/16kn88Wkdr5E7fyDTOJDQEFU_1l0g4G0k/view" target="_blank" rel="noopener noreferrer">
                CV
              </a>
            </Stack>
          </Hidden>

          <Hidden smUp>
            <PopupState variant="popover" popupId="header-menu">
              {(popupState) => (
                <>
                  <IconButton variant="contained" {...bindTrigger(popupState)}>
                    <MenuIcon sx={{ color: theme.palette.text.primary }} />
                  </IconButton>
                  <Menu {...bindMenu(popupState)}
                    PaperProps={{
                      sx: {
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                      }
                    }}
                  >
                    <MenuItem component="a" href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); popupState.close(); }}>
                      About
                    </MenuItem>
                    <MenuItem component="a" href="#projects" onClick={(e) => { e.preventDefault(); scrollToSection('projects'); popupState.close(); }}>
                      Projects
                    </MenuItem>
                    <MenuItem component="a" href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); popupState.close(); }}>
                      Contact
                    </MenuItem>
                    <MenuItem
                      component="a"
                      href="https://drive.google.com/file/d/16kn88Wkdr5E7fyDTOJDQEFU_1l0g4G0k/view"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={popupState.close}
                    >
                      CV
                    </MenuItem>
                  </Menu>
                </>
              )}
            </PopupState>
          </Hidden>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={4}
          sx={{ width: '100%', textAlign: { xs: 'center', sm: 'left' } }}
        >
          <motion.div initial="hidden" animate="visible" variants={textVariants} style={{ flex: 1 }}>
            <Stack
              color={theme.palette.text.primary}
              spacing={2}
              alignItems={{ xs: 'center', sm: 'flex-start' }}
            >
              <Typography fontSize={{ xs: 30, sm: 40 }} fontWeight={800} component="h1">
                <Span title="Hi" /> There, 👋
              </Typography>
              <Typography fontSize={{ xs: 30, sm: 40 }} fontWeight={800} component="h1">
                I'm Nijat <Span title="Zeynalli" />
              </Typography>
              <Typography fontSize={{ xs: 16, sm: 20 }} fontWeight={800} component="p">
                I'm Into <Span title="" /><span ref={typedElement} style={{ color: theme.palette.primary.main }}></span>
              </Typography>
              <Social isWhite={true} />
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.text.primary,
                    padding: '10px 25px',
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      boxShadow: `0px 4px 10px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                  }}
                  variant="contained"
                  size="large"
                >
                  <a style={{ color: theme.palette.text.primary, textDecoration: 'none' }} href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>
                    Contact Me
                  </a>
                </Button>
              </motion.div>
            </Stack>
          </motion.div>

          <Hidden smDown>
            <motion.div initial="hidden" animate="visible" variants={imageVariants} style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <Box>
                <img
                  src={manUsingLaptop}
                  style={{ width: "300px", height: 'auto', borderRadius: "50%", objectFit: 'cover', boxShadow: `0px 0px 25px ${alpha(theme.palette.primary.main, 0.3)}` }}
                  alt="Man using laptop"
                />
              </Box>
            </motion.div>
          </Hidden>
        </Stack>
      </Container>
    </Stack>
  );
}

// About component
const About = () => {
  const theme = useTheme();

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  const imageVariants = {
    hidden: { opacity: 0, x: -50, scale: 0.9 },
    visible: { opacity: 1, x: 0, scale: 1, transition: { duration: 0.8, delay: 0.2, ease: "easeOut" } },
  };

  const textVariants = {
    hidden: { opacity: 0, x: 50, scale: 0.9 },
    visible: { opacity: 1, x: 0, scale: 1, transition: { duration: 0.8, delay: 0.4, ease: "easeOut" } },
  };

  return (
    <Stack
      bgcolor={theme.palette.background.paper} 
      id="about"
      py={{ xs: 8, md: 12 }}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      sx={{
        backgroundImage: `radial-gradient(at 100% 100%, ${alpha(theme.palette.secondary.main, 0.05)} 0%, transparent 70%)`,
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Container maxWidth="md">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={sectionVariants}>
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div variants={imageVariants}>
                <Box
                  position="relative"
                  sx={{
                    overflow: "hidden",
                    borderRadius: "15px",
                    boxShadow: `0px 8px 25px ${alpha(theme.palette.text.primary, 0.2)}`,
                    '&:hover img': {
                      transform: 'scale(1.08)', // More pronounced zoom
                    },
                    border: `2px solid ${theme.palette.primary.main}`, // Accent border
                  }}
                >
                  <img
                    src={profile}
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "cover",
                      borderRadius: "15px",
                      transition: "transform 0.5s ease-in-out", // Slightly slower transition
                    }}
                    alt="Nijat Zeynalli"
                  />
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div variants={textVariants}>
                <Stack spacing={3}>
                  <Typography
                    variant="h4"
                    color={theme.palette.text.primary}
                    fontWeight={700}
                    mb={1}
                    sx={{ borderLeft: `5px solid ${theme.palette.primary.main}`, pl: 2 }}
                  >
                    About Me
                  </Typography>
                  <Typography variant="body1" fontSize={16} lineHeight={1.8} color={theme.palette.text.secondary}>
                    My name is Nijat Zeynalli, born on April 13, 2004, in Baku, Azerbaijan. I am currently pursuing a Bachelor’s degree in Computer Science at the French-Azerbaijani University (UFAZ), a joint initiative of the University of Strasbourg and Azerbaijan State Oil and Industry University. I was admitted to the university based on my high score in the national entrance examination and successful performance in the university’s internal examination.

Alongside my academic studies, I have continuously developed my technical skills and gained practical experience in software development. I completed a Front-End Development course at Jed Academy and later specialized in Android Development with Kotlin at Matrix Academy. I also achieved an IELTS Academic score of 7.5, corresponding to a C1 level of English proficiency.

Before starting my professional internships, I became involved in Act2Act, a startup project where I am still actively contributing. The project has progressed to production and includes a web application as well as cross-platform mobile applications developed using Kotlin Multiplatform (KMP/CMP). As a member of the mobile development team, I have contributed to the development and improvement of the mobile applications, gaining valuable experience in building and maintaining a real-world product from development through production.

I then began my professional experience with an Android Development internship at Kapital Bank, where I gained hands-on experience in mobile application development and learned how to apply my technical knowledge in a large professional environment. Following this experience, I completed an internship as an Android Developer at Crocusoft, where I further strengthened my expertise in Android development, Kotlin, and professional software engineering practices.

Currently, I am working as a Junior Software Developer at EPAM Systems, where I continue to expand my technical knowledge and gain experience working on professional software development projects. My academic background, startup experience, internships at Kapital Bank and Crocusoft, and current role at EPAM Systems have allowed me to develop a strong foundation in software engineering and deepen my passion for building reliable, scalable, and user-focused software solutions.
                  </Typography>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Social />
                </Stack>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Stack>
  );
};


function Project() {
  const theme = useTheme();
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    const unsub = onSnapshot(collectionRef, (querySnapshot) => {
      const items = [];
      querySnapshot.forEach((doc) => {
        items.push(doc.data());
      });
      setProjects(items);
    });
    return () => unsub();
  }, []);

  const projectCardVariants = {
    hidden: { opacity: 0, y: 70, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, ease: "easeOut", staggerChildren: 0.2 } },
  };

  const projectItemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  return (
    <Stack id="projects"
      bgcolor={theme.palette.background.default}
      py={{ xs: 8, md: 12 }}
      sx={{
        backgroundImage: `radial-gradient(at 0% 100%, ${alpha(theme.palette.secondary.main, 0.05)} 0%, transparent 70%)`,
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={8}>
          <Typography
            fontSize={{ xs: 28, sm: 36 }}
            fontWeight={800}
            alignSelf="center"
            component="h1"
            color={theme.palette.text.primary}
          >
            My <Span title="Projects" />
          </Typography>
          {projects.map((project, index) => (
            <motion.div key={index} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={projectCardVariants}>
              <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center" flexDirection={index % 2 === 0 ? 'row' : 'row-reverse'}>
                <Grid item xs={12} md={6}>
                  <motion.div variants={projectItemVariants}>
                    <Box
                      sx={{
                        borderRadius: '15px',
                        overflow: 'hidden',
                        boxShadow: `0px 8px 25px ${alpha(theme.palette.text.primary, 0.2)}`,
                        border: `2px solid ${theme.palette.secondary.main}`, // Secondary accent border
                        '& img': {
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          objectFit: 'cover',
                          transition: 'transform 0.5s ease-in-out',
                        },
                        '&:hover img': {
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      <img
                        src={project.img}
                        alt={project.title}
                      />
                    </Box>
                  </motion.div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <motion.div variants={projectItemVariants}>
                    <Stack spacing={2}>
                      <Typography fontWeight={900} variant="h5" component="h2" color={theme.palette.text.primary}>
                        {project.title}
                      </Typography>
                      <Typography fontSize={15} component="p" color={theme.palette.text.secondary}>
                        {project.description}
                      </Typography>
                      <Box>
                        <Typography fontWeight={700} fontSize={14} color={theme.palette.text.primary}>
                          Technologies Used:
                        </Typography>
                        {project.technolgy && project.technolgy.length > 0 && (
                          <Typography fontSize={14} color={theme.palette.text.secondary}>
                            {project.technolgy.join(" | ")}
                          </Typography>
                        )}
                      </Box>
                      <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                        <IconButton
                          href={project.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="GitHub Repository"
                          sx={{ color: theme.palette.text.primary }}
                        >
                          <GitHubIcon fontSize="large" /> <Typography ml={1} color={theme.palette.text.primary}>View Code</Typography>
                        </IconButton>
                      </motion.div>
                      <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                    </Stack>
                  </motion.div>
                </Grid>
              </Grid>
            </motion.div>
          ))}
        </Stack>
      </Container>
    </Stack>
  );
}


const Contact = () => {
  const theme = useTheme();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const formRef = useRef(null);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!name || !email || !message) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Please fill in all required fields.');
      setOpenSnackbar(true);
      return;
    }

    const templateParams = {
      from_name: name,
      reply_to: email,
      message: message
    };

    const serviceID = 'service_sr0a9pu';
    const templateID = 'template_wn69xbc';
    const publicKey = 'CSEQbY6SS_wX2QWM1';
    emailjs.send(serviceID, templateID, templateParams, publicKey)
      .then((response) => {
        console.log('Email sent successfully:', response);
        setSnackbarSeverity('success');
        setSnackbarMessage('Email sent successfully!');
        setOpenSnackbar(true);
        setName('');
        setEmail('');
        setMessage('');
      })
      .catch((error) => {
        console.error('Error sending email:', error);
        setSnackbarSeverity('error');
        setSnackbarMessage('There was an error sending your message. Please try again later.');
        setOpenSnackbar(true);
      });
  };

  const contactVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  return (
    <Stack
      id="contact"
      bgcolor={theme.palette.background.default} // Use default background
      justifyContent="center"
      alignItems="center"
      py={{ xs: 8, md: 12 }}
      sx={{
        backgroundImage: `linear-gradient(to top right, ${alpha(theme.palette.primary.main, 0.05)}, transparent 50%), linear-gradient(to bottom left, ${alpha(theme.palette.secondary.main, 0.05)}, transparent 50%)`,
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Container maxWidth="sm">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={contactVariants}>
          <Typography
            fontSize={{ xs: 28, sm: 36 }}
            fontWeight={800}
            alignSelf="center"
            component="h1"
            gutterBottom
            textAlign="center"
            color={theme.palette.text.primary}
            mb={4}
          >
            Get In <Span title="Touch" /> 📧
          </Typography>
          <Box
            component="form"
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit}
            ref={formRef}
            sx={{
              p: { xs: 2, sm: 4 },
              borderRadius: '15px',
              boxShadow: `0px 10px 30px ${alpha(theme.palette.text.primary, 0.15)}`,
              bgcolor: theme.palette.background.paper, // Use paper background for form
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`, // Subtle border
            }}
          >
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Your Name"
                variant="outlined"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <TextField
                fullWidth
                label="Your Email"
                variant="outlined"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                fullWidth
                label="Your Message"
                variant="outlined"
                multiline
                rows={5}
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  size="large"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.text.primary,
                    padding: '12px 0',
                    borderRadius: '8px',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      boxShadow: `0px 4px 10px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                  }}
                >
                  Send
                </Button>
              </motion.div>
            </Stack>
          </Box>
          <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(false)}>
            <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </motion.div>
      </Container>
    </Stack>
  );
};


function Footer() {
  const theme = useTheme();
  return (
    <Stack
      bgcolor={theme.palette.background.default}
      color={theme.palette.text.primary}
      justifyContent="center"
      alignItems="center"
      py={4}
      sx={{
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Container maxWidth="md">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={{ xs: 3, sm: 0 }}
        >
          <Typography
            fontSize={{ sm: 25, xs: 20 }}
            fontWeight={900}
            color={theme.palette.text.primary}
            mb={{ xs: 2, sm: 0 }}
          >
            <a style={{ color: theme.palette.text.primary, textDecoration: 'none' }} href="/">
              Nijat <Span title="Zeynalli" />
            </a>
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            sx={{
              "& >a": {
                textDecoration: "none",
                color: theme.palette.text.primary,
                fontSize: "16px",
                position: 'relative',
                paddingBottom: '5px',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  width: '0',
                  height: '2px',
                  bottom: '0',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: theme.palette.primary.main,
                  transition: 'width 0.3s ease-in-out',
                },
                '&:hover::after': {
                  width: '100%',
                },
              },
            }}
          >
            <a href="#about">About</a>
            <a href="#projects">Projects</a>
            <a href="#contact">Contact me</a>
          </Stack>
          <Social isWhite={true} />
        </Stack>
        <Typography mt={4} textAlign="center" fontSize={12} color={theme.palette.text.disabled}>
          &copy; {new Date().getFullYear()} Nijat Zeynalli. All rights reserved.
        </Typography>
      </Container>
    </Stack>
  );
}

function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline /> {/* Applies baseline CSS to match theme */}
      <Stack fontFamily="Roboto, sans-serif"> {/* Changed font for modern look */}
        <Header />
        <About />
        <Project />
        <Contact />
        <Footer />
      </Stack>
    </ThemeProvider>
  );
}

export default App;
